import Box from "@mui/material/Box";
import Checkbox from "@mui/material/Checkbox";
import Chip from "@mui/material/Chip";
import CircularProgress from "@mui/material/CircularProgress";
import Link from "@mui/material/Link";
import ListItemText from "@mui/material/ListItemText";
import MenuItem from "@mui/material/MenuItem";
import Stack from "@mui/material/Stack";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import { useQuery } from "@tanstack/react-query";
import { ExternalLink, SlidersHorizontal } from "lucide-mui";
import type { ReactNode } from "react";
import { Link as RouterLink } from "react-router-dom";
import { api, type ProspectCompanyFacts, type ProspectDetail } from "../../api/client.ts";
import { keys } from "../../api/queryKeys.ts";
import { useTenant } from "../../contexts/TenantContext.tsx";
import { KIND_LABELS } from "../../pages/company-detail/registryEventKinds.ts";
import { formatCompactMoney, formatNumber } from "../../utils/format.ts";
import DetailRow from "../DetailRow.tsx";
import DropdownMenu from "../DropdownMenu.tsx";
import { FoldList, FoldRow } from "../FoldList.tsx";
import FormattedDate from "../FormattedDate.tsx";
import SectionHeading from "../SectionHeading.tsx";
import ScrollTable from "../table/ScrollTable.tsx";
import {
  COMPANY_DETAIL_SECTIONS,
  type CompanyDetailSection,
  useCompanyDetailSections,
  useExpandedCompanyDetails,
} from "./companyDetailSections.ts";

// How many filings, job ads and events an opened row lists; the company page has the rest.
const SHOWN = 5;

/** The address to open: the stored URL, else the domain; http(s) only. */
function websiteHref(url: string): string {
  return /^https?:\/\//i.test(url) ? url : `https://${url}`;
}

/** The company's website, or its domain, or an unresolved entry's snapshot. */
export function prospectWebsite(prospect: ProspectDetail): string | null {
  return (
    prospect.company?.website ??
    prospect.company_domain ??
    prospect.collections.find((c) => c.company_website)?.company_website ??
    null
  );
}

/** The website as a link that opens in a new tab, shown bare ("acme.no/en"). */
export function WebsiteLink({ url }: { url: string }) {
  const href = websiteHref(url);
  const label = href.replace(/^https?:\/\/(www\.)?/i, "").replace(/\/$/, "");
  return (
    <Link
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      variant="body2"
      sx={{ display: "inline-flex", alignItems: "center", gap: 0.5, minWidth: 0 }}
    >
      <Box component="span" sx={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
        {label}
      </Box>
      <ExternalLink fontSize="inherit" />
    </Link>
  );
}

function Loading() {
  return <CircularProgress size={16} />;
}

function Empty({ children }: { children: ReactNode }) {
  return (
    <Typography variant="body2" sx={{ color: "text.secondary" }}>
      {children}
    </Typography>
  );
}

/** A labelled row of chips (industries, who it sells to); nothing when empty. */
function ChipRow({ label, values }: { label: string; values: string[] }) {
  if (values.length === 0) return null;
  return (
    <Stack direction="row" spacing={1} sx={{ alignItems: "baseline" }}>
      <Typography variant="caption" sx={{ color: "text.secondary", width: 64, flexShrink: 0 }}>
        {label}
      </Typography>
      <Stack direction="row" spacing={0.5} useFlexGap sx={{ flexWrap: "wrap" }}>
        {values.map((v) => (
          <Chip key={v} size="small" variant="outlined" label={v} />
        ))}
      </Stack>
    </Stack>
  );
}

function KeyFacts({ company }: { company: ProspectCompanyFacts }) {
  const hasMarket =
    company.ai_industries.length + company.ai_served_verticals.length + company.ai_customer_type.length > 0;
  return (
    <Stack spacing={1.5}>
      {hasMarket && (
        <Stack spacing={0.75}>
          <ChipRow label="Industries" values={company.ai_industries} />
          <ChipRow label="Serves" values={company.ai_served_verticals} />
          <ChipRow label="Sells to" values={company.ai_customer_type} />
        </Stack>
      )}
      <Box sx={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(140px, 1fr))", columnGap: 2 }}>
        <DetailRow label="Org number" value={company.org_number} />
        <DetailRow label="Org form" value={company.org_form} />
        <DetailRow
          label="Employees"
          value={company.employee_count != null ? formatNumber(company.employee_count) : null}
        />
        <DetailRow label="LinkedIn" value={company.linkedin_url && <WebsiteLink url={company.linkedin_url} />} />
      </Box>
    </Stack>
  );
}

function Financials({ companyId }: { companyId: string }) {
  const { tenantPath } = useTenant();
  const { data, isLoading } = useQuery({
    queryKey: keys.masterCompany.financials(companyId),
    queryFn: () => api.getCompanyFinancials(companyId),
  });
  if (isLoading) return <Loading />;
  if (!data?.length) return <Empty>No annual accounts on record.</Empty>;
  const cell = { px: 0.75, py: 0.5, "&:first-of-type": { pl: 0 }, "&:last-of-type": { pr: 0 } };
  return (
    <>
      <ScrollTable sx={{ "& td, & th": cell }}>
        <TableHead>
          <TableRow>
            <TableCell>Year</TableCell>
            <TableCell align="right">Revenue</TableCell>
            <TableCell align="right">Operating result</TableCell>
            <TableCell align="right">Net income</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {data.slice(0, SHOWN).map((f) => (
            <TableRow key={f.id}>
              <TableCell>{f.fiscal_year}</TableCell>
              <TableCell align="right">{formatCompactMoney(f.revenue, f.currency)}</TableCell>
              <TableCell align="right">{formatCompactMoney(f.operating_result, f.currency)}</TableCell>
              <TableCell align="right">{formatCompactMoney(f.net_income, f.currency)}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </ScrollTable>
      <Link component={RouterLink} to={tenantPath(`/companies/${companyId}`)} variant="caption">
        All financials
      </Link>
    </>
  );
}

function Technologies({ companyId }: { companyId: string }) {
  const { data, isLoading } = useQuery({
    queryKey: keys.masterCompany.technologies(companyId),
    queryFn: () => api.getCompanyTechnologies(companyId),
  });
  if (isLoading) return <Loading />;
  if (!data?.length) return <Empty>No technologies detected.</Empty>;
  return (
    <Stack direction="row" spacing={0.5} useFlexGap sx={{ flexWrap: "wrap" }}>
      {data.map((tech) => (
        <Tooltip key={tech.id} title={tech.category ?? ""}>
          <Chip size="small" variant="outlined" label={tech.version ? `${tech.name} ${tech.version}` : tech.name} />
        </Tooltip>
      ))}
    </Stack>
  );
}

function About({ summary }: { summary: string }) {
  return (
    <Typography variant="body2" sx={{ whiteSpace: "pre-wrap" }}>
      {summary}
    </Typography>
  );
}

function JobAds({ companyId }: { companyId: string }) {
  const { data, isLoading } = useQuery({
    queryKey: keys.masterCompany.jobAds(companyId),
    queryFn: () => api.getCompanyJobAds(companyId),
  });
  if (isLoading) return <Loading />;
  if (!data?.length) return <Empty>No job ads on record.</Empty>;
  return (
    <Stack spacing={0.5}>
      {data.slice(0, SHOWN).map((ad) => (
        <Stack key={ad.id} direction="row" spacing={1} sx={{ alignItems: "baseline", minWidth: 0 }}>
          <Link component={RouterLink} to={`/job-ads/${ad.id}`} variant="body2" noWrap sx={{ minWidth: 0 }}>
            {ad.title}
          </Link>
          {ad.published_at && (
            <Typography variant="caption" sx={{ color: "text.secondary", flexShrink: 0 }}>
              <FormattedDate value={ad.published_at} />
            </Typography>
          )}
        </Stack>
      ))}
    </Stack>
  );
}

function RegistryEvents({ companyId }: { companyId: string }) {
  const { data, isLoading } = useQuery({
    queryKey: keys.masterCompany.registryEvents(companyId),
    queryFn: () => api.getCompanyRegistryEvents(companyId),
  });
  if (isLoading) return <Loading />;
  if (!data?.length) return <Empty>No company events on record.</Empty>;
  return (
    <Stack spacing={0.5}>
      {data.slice(0, SHOWN).map((e) => (
        <Stack key={e.id} direction="row" spacing={1} sx={{ alignItems: "baseline", minWidth: 0 }}>
          <Typography variant="caption" sx={{ color: "text.secondary", flexShrink: 0 }}>
            <FormattedDate value={e.registered_on} />
          </Typography>
          <Typography variant="body2" noWrap sx={{ minWidth: 0 }}>
            {e.kinds.map((k) => KIND_LABELS[k] ?? k).join(", ") || e.title || e.notice_type}
          </Typography>
        </Stack>
      ))}
    </Stack>
  );
}

interface Section {
  summary?: ReactNode;
  body: ReactNode;
}

/** Each section's folded gist and opened content; null when there is nothing to show. */
function section(key: CompanyDetailSection, companyId: string, company: ProspectCompanyFacts): Section | null {
  switch (key) {
    case "facts": {
      const gist = [
        company.ai_customer_type.join(", "),
        company.ai_industries.slice(0, 2).join(", "),
        company.employee_count != null && `${formatNumber(company.employee_count)} employees`,
      ].filter(Boolean);
      const any = gist.length > 0 || company.ai_served_verticals.length > 0 || company.org_number || company.org_form || company.linkedin_url;
      return any ? { summary: gist.join(" · "), body: <KeyFacts company={company} /> } : null;
    }
    case "financials":
      return {
        summary:
          company.revenue != null
            ? `Revenue ${formatCompactMoney(company.revenue, company.currency)}${company.fiscal_year ? ` · ${company.fiscal_year}` : ""}`
            : undefined,
        body: <Financials companyId={companyId} />,
      };
    case "technologies":
      return company.technology_count > 0
        ? { summary: formatNumber(company.technology_count), body: <Technologies companyId={companyId} /> }
        : null;
    case "summary":
      return company.ai_summary ? { summary: company.ai_summary, body: <About summary={company.ai_summary} /> } : null;
    case "job_ads":
      return { body: <JobAds companyId={companyId} /> };
    case "registry_events":
      return { body: <RegistryEvents companyId={companyId} /> };
  }
}

/**
 * What the database holds on the prospect's company, one folded line per
 * detail. The person picks which from the heading's menu, and what they show
 * and open stays that way next time. Opened rows load their data then, not before.
 */
export default function ProspectCompanyDetails({
  companyId,
  company,
}: {
  companyId: string;
  company: ProspectCompanyFacts;
}) {
  const [shown, setShown] = useCompanyDetailSections();
  const [expanded, setExpanded] = useExpandedCompanyDetails();
  // Sections with something to show; the menu offers these, the list shows the picked ones.
  const available = COMPANY_DETAIL_SECTIONS.map((s) => ({ ...s, content: section(s.key, companyId, company) })).filter(
    (s): s is typeof s & { content: Section } => s.content !== null,
  );
  if (available.length === 0) return null;
  const rows = available.filter((s) => shown.includes(s.key));
  const toggle = (list: CompanyDetailSection[], key: CompanyDetailSection) =>
    list.includes(key) ? list.filter((k) => k !== key) : [...list, key];
  return (
    <Box>
      <SectionHeading
        title="Company"
        action={
          <DropdownMenu
            icon={<SlidersHorizontal fontSize="small" />}
            aria-label="Choose company details"
            tooltip="Choose details"
            align="right"
            buttonProps={{ size: "small" }}
          >
            {available.map(({ key, label }) => (
              <MenuItem
                key={key}
                dense
                onClick={(e) => {
                  e.stopPropagation();
                  setShown(toggle(shown, key));
                }}
              >
                <Checkbox size="small" checked={shown.includes(key)} tabIndex={-1} disableRipple sx={{ p: 0, mr: 1 }} />
                <ListItemText>{label}</ListItemText>
              </MenuItem>
            ))}
          </DropdownMenu>
        }
      />
      {rows.length === 0 ? (
        <Empty>No details picked; choose some with the button above.</Empty>
      ) : (
        <FoldList>
          {rows.map(({ key, label, content }) => (
            <FoldRow
              key={key}
              label={label}
              summary={content.summary}
              expanded={expanded.includes(key)}
              onToggle={() => setExpanded(toggle(expanded, key))}
            >
              {content.body}
            </FoldRow>
          ))}
        </FoldList>
      )}
    </Box>
  );
}
